import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "@/domain/constants/routes";
import { useUserContext } from "@/application/services/userContextService";
import { logger } from "@/infrastructure/logger";

// 創建智能路由專用的日誌器
const log = logger.createLogger("SmartRouter");

/**
 * 智能路由組件
 * 根據用戶狀態導向到適當的頁面
 */
export function SmartRouter({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { context: userContext, isLoading, refreshContext } = useUserContext();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // 如果用戶不在 HOME 頁面，不需要重定向
    if (location.pathname !== ROUTES.HOME) {
      setIsChecking(false);
      return;
    }

    const checkUserContext = async () => {
      try {
        log.info("SmartRouter: 檢查用戶上下文");
        // 如果用戶上下文正在加載中，設置一個最長等待時間
        if (isLoading) {
          log.info("用戶上下文正在加載中，設置最長等待時間");
          // 設置一個超時，避免永久等待
          setTimeout(() => {
            if (isChecking) {
              log.warn("上下文加載超時，繼續檢查流程");
              setIsChecking(false);
            }
          }, 3000); // 3秒後超時
          return;
        }

        // 定義一個函數來檢查上下文是否有效
        const isValidContext = (context: any) => {
          return context && Object.keys(context).length > 0;
        };

        // 先嘗試使用現有的 userContext
        let contextToUse = userContext;
        log.debug("當前用戶上下文", userContext);

        // 檢查用戶上下文是否為空或無效
        const hasValidContext = isValidContext(contextToUse);

        // 如果是新用戶，導向到聊天建站頁面
        log.debug("hasValidContext", hasValidContext);
        if (location.pathname == ROUTES.HOME) {
          return;
        }
        if (hasValidContext && contextToUse.is_new_user === true) {
          log.info("新用戶，導向到聊天建站頁面");
          navigate(ROUTES.CONVERSATION, { replace: true });
        }
        // 其他情況保持在 HOME 頁面
        else if (hasValidContext) {
          log.info("保持在 HOME 頁面");
          // 已經在 HOME 頁面，不需要重定向
          // 只需設置檢查完成狀態
          setIsChecking(false);
        } else {
          // 如果沒有有效的上下文，可能是因為用戶是首次登入或者上下文已過期
          log.warn("沒有有效的用戶上下文，導向到登入頁面");
          navigate(ROUTES.LOGIN, { replace: true });
        }
      } catch (error) {
        log.error("Error checking user context", error);
        // 發生錯誤時，保持在當前頁面
        setIsChecking(false);
      } finally {
        // 確保無論如何都會設置檢查完成狀態（除非提前返回）
        setIsChecking(false);
      }
    };

    checkUserContext();
  }, [navigate, location.pathname, userContext, isLoading, refreshContext]);

  // 在檢查過程中顯示載入中狀態
  if (isChecking || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-gray-700 dark:text-gray-300">
          載入中...
        </div>
      </div>
    );
  }

  // 檢查完成後，顯示子組件
  return children;
}
